'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import useSWR, { useSWRConfig, type Cache, type ScopedMutator } from 'swr'
import { SWRGlobalState } from 'swr/_internal'

import {
  canAdmitPreviewSource,
  getPreviewSourceBudgetVersion,
  measurePreviewSourceBytes,
  observePreviewElementVisibility,
  observePreviewSource,
  releasePreviewSource,
  releasePreviewSourceReservation,
  reservePreviewSourceFetch,
  retainPreviewSource,
  subscribePreviewSourceBudget,
} from '@/lib/preview-memory-budget'

// Default cap for plain text / code / markdown / json source previews.
const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024

// Larger cap for rendered HTML artifact previews. Agent-generated HTML
// frequently inlines screenshots as base64 (which inflates binary by ~33%),
// so the 2 MB text cap is too tight in practice. Kept in sync with the
// backend share `_MAX_HTML_BODY_BYTES` (apps/api/routers/share_public.py).
// Tune here if image-heavy artifacts still overshoot.
export const HTML_PREVIEW_MAX_BYTES = 25 * 1024 * 1024

// Abort fetches that never settle (hung connection on a weak network) so
// they surface as network errors instead of an endless spinner.
const PREVIEW_FETCH_TIMEOUT_MS = 30_000

// Automatic retries are capped at 2 and limited to network failures:
// retrying an HTTP error or an oversize file rarely helps and would only
// amplify load on the serve proxy.
const NETWORK_ERROR_MAX_RETRIES = 2

/** Stable no-op used while the hook has no URL (and therefore no cache key). */
const NOOP_EVICT = (): void => {}

export type FilePreviewErrorReason =
  | 'network'
  | 'http'
  | 'too_large'
  | 'deferred'

/**
 * Classified preview fetch failure. ``reason`` lets the UI pick a recovery
 * path: ``network`` is usually transient (auto-retried, "unstable
 * connection" copy), while ``http`` / ``too_large`` point at the file or
 * its URL and keep the generic copy. ``status`` is set for ``http`` only.
 */
export class FilePreviewError extends Error {
  readonly reason: FilePreviewErrorReason
  readonly status?: number

  constructor(
    reason: FilePreviewErrorReason,
    message: string,
    status?: number,
  ) {
    super(message)
    this.name = 'FilePreviewError'
    this.reason = reason
    this.status = status
  }
}

/** True when the preview failure is a (usually transient) network problem. */
export function isNetworkPreviewError(error: unknown): boolean {
  return error instanceof FilePreviewError && error.reason === 'network'
}

/**
 * Classify a fetch() rejection: a TypeError (DNS / connection reset /
 * CORS), a DOMException TimeoutError (from AbortSignal.timeout) or
 * AbortError, or the browser reporting itself offline all mean the bytes
 * never arrived. ``navigator.onLine`` is only a supporting signal — it
 * cannot distinguish a dead proxy from a dead connection — so the thrown
 * error type is the primary test.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true
  }
  if (error instanceof TypeError) return true
  if (
    error instanceof DOMException &&
    (error.name === 'TimeoutError' || error.name === 'AbortError')
  ) {
    return true
  }
  return false
}

/**
 * Auto-retry policy for a failed preview fetch. Returns the backoff delay in
 * ms, or null when the failure must surface immediately.
 *
 * Only ``network`` failures are retried (transient by nature) and only
 * {@link NETWORK_ERROR_MAX_RETRIES} times: retrying an HTTP error or an
 * oversize file rarely helps and would only amplify load on the serve proxy.
 * Backoff is exponential from 1s (1s, then 2s, then stop).
 *
 * ``retryCount`` is SWR's 1-based attempt counter.
 */
export function nextPreviewRetryDelayMs(
  error: unknown,
  retryCount: number,
): number | null {
  if (!isNetworkPreviewError(error)) return null
  if (retryCount > NETWORK_ERROR_MAX_RETRIES) return null
  return 1000 * 2 ** (retryCount - 1)
}

/**
 * SWR cache key for a {@link useFilePreviewText} entry — single source of
 * truth shared by the hook and by external cache eviction (e.g.
 * ``MediaPreviewDialog`` releasing the HTML source string on close). The
 * key varies by cap so a cached "too large" failure under a smaller cap is
 * not reused for a caller that allows more bytes.
 */
export function filePreviewTextKey(url: string, maxBytes: number): string {
  return `file-preview-text:${maxBytes}:${url}`
}

/**
 * Drop SWR's per-key *concurrent-request* markers.
 *
 * SWR keeps two independent halves of state per key: the cached value, and the
 * request bookkeeping (``FETCH`` — the in-flight/just-finished request used for
 * deduplication — plus ``PRELOAD``). ``mutate(key, undefined, …)`` only ever
 * touches the first half; the markers are cleared inside ``internalMutate``'s
 * ``startRevalidate``, which runs **only** on the ``revalidate !== false``
 * branch. So a release-the-string eviction leaves the second half pointing at a
 * request whose result SWR itself will refuse to commit (its mutation guard
 * discards any response that started before the last mutation), and the next
 * mount inside ``dedupingInterval`` dedupes onto exactly that record: no new
 * request, no data, no error, not loading. That is the stg
 * "Cannot load preview" on the second open — the preview had been evicted, and
 * SWR had been told to forget the bytes but not the request.
 *
 * Clearing the markers is what makes the eviction complete, and it is the only
 * formulation that NEVER issues a request. Two alternatives look simpler and
 * are not:
 * - ``revalidate: true`` does delete the markers, but it also refetches
 *   whenever a revalidator is still registered for the key. Today none is
 *   (eviction runs at ``refCount === 0``), so it would appear to work — while
 *   silently meaning "on close, blank the viewer and re-pull up to 25 MiB" for
 *   the first future caller that keeps a consumer mounted.
 * - Shrinking ``dedupingInterval`` cannot help at all: StrictMode's
 *   mount → cleanup → mount happens inside a single task, so the marker's
 *   cleanup timer never gets to run between them.
 *
 * ``SWRGlobalState`` is swr's own typed registry (``GlobalState`` is an
 * annotated export tuple; swr's ``preload`` reads ``PRELOAD`` from it the same
 * way), and it is keyed by cache instance — hence ``cache``, taken from the
 * consumer's own ``useSWRConfig()``, so a scoped provider can never be
 * desynced by writing to the default cache instead.
 *
 * Degrades to a no-op if the layout ever changes under a swr upgrade rather
 * than throwing, because throwing here would turn "the preview does not load"
 * into "the dialog crashes". The safety net for that silence is behavioural,
 * not defensive: ``tests/unit/file-preview-reopen-lifecycle.test.tsx`` drives a
 * real open → close → reopen through a real ``useSWR`` and goes red the moment
 * this stops taking effect.
 */
function invalidateSwrRequestMarkers(cache: Cache, key: string): void {
  // Retire whichever fetch currently owns the key, so a request abandoned by
  // this eviction can no longer report an error against it (see
  // {@link inFlightPreviewFetches}).
  inFlightPreviewFetches.delete(key)
  const state = SWRGlobalState.get(cache)
  if (!state) return
  const [, , FETCH, PRELOAD] = state
  delete FETCH[key]
  delete PRELOAD[key]
}

/**
 * The fetch that currently *owns* each key, identified by a per-call ticket.
 *
 * SWR's two settle paths are not symmetric, and only one of them is fenced. The
 * success path bails out on `!FETCH[key] || FETCH[key][1] !== startAt` and
 * returns before writing anything, so a stale response can never commit. The
 * catch path has no such check: it sets `finalState.error` unconditionally and
 * the shared `finishRequestAndUpdateState()` writes it into the cache entry for
 * that key. A request the user abandoned by closing the dialog — typically
 * failing later at the 30s timeout — would therefore land its error on the key
 * a REOPENED preview is already displaying, and the error-first branch would
 * flip a working preview back to "Cannot load preview".
 *
 * That path only became reachable once eviction started letting the reopen
 * fetch again, so it is fenced here rather than left to SWR: a fetch may report
 * a failure only while it still holds the key's ticket. Eviction drops the
 * ticket, and a newer fetch replaces it.
 *
 * A Map keyed by cache key, holding one identity object per in-flight fetch.
 * Self-bounding — every entry is removed by its own `finally` — so unlike a
 * generation counter it needs no size cap, and cannot mistake a live fetch for
 * a stale one after a trim (which would swallow a REAL error and resurrect the
 * silent no-data-no-error state this whole fix exists to remove).
 */
const inFlightPreviewFetches = new Map<string, object>()

/**
 * Value returned instead of rethrowing when a fetch has lost its key.
 *
 * Never rendered: by construction the key's request marker was either deleted
 * (eviction) or replaced by a newer fetch, so SWR's success-side staleness
 * check discards this before it can reach the cache. Returning is the only way
 * to stay silent — anything thrown from a fetcher becomes `error` on the key.
 */
const SUPERSEDED_PREVIEW_FETCH = ''

/**
 * Build the single-key SWR eviction for one preview text entry.
 *
 * This is the ONLY place the eviction call is written: both the budget
 * registry (which stores it per entry and runs it on LRU eviction) and
 * ``MediaPreviewDialog``'s unmount-time request bind through here, so the
 * eviction shape cannot drift between the two call sites. Both therefore get
 * the complete eviction — the LRU path evicts 2 MiB markdown/code entries and
 * had the identical second-mount defect.
 *
 * Both halves run synchronously inside this closure (``mutate``'s
 * ``undefined``-data path reaches its cache write with no ``await`` in
 * between), so no mount — not even StrictMode's immediate remount — can observe
 * a half-evicted key.
 *
 * ``revalidate: false`` is mandatory — eviction must release the string, never
 * trigger a refetch. Exact key, no matcher: the documented §2.5 carve-out for
 * deliberate single-key cache eviction
 * (``docs/engineering/best-practices/web-frontend-architecture.md``).
 */
export function bindFilePreviewTextEviction(
  mutate: ScopedMutator,
  key: string,
  cache: Cache,
): () => void {
  return () => {
    invalidateSwrRequestMarkers(cache, key)
    void mutate(key, undefined, { revalidate: false })
  }
}

function tooLargeError(cap: number): FilePreviewError {
  const mb = Math.round(cap / (1024 * 1024))
  return new FilePreviewError(
    'too_large',
    `Attachment too large to preview inline (over ${mb} MB).`,
  )
}

/** A decoded preview body plus the exact number of bytes it cost to fetch. */
interface CappedBody {
  text: string
  /**
   * Wire bytes accumulated by the streaming reader — the number the memory
   * budget accounts. Deliberately NOT `text.length`: that counts UTF-16 code
   * units, which overstates ASCII-dominant HTML by ~2x and understates
   * astral-plane content.
   */
  bytes: number
}

/**
 * Read a fetch Response body up to `cap` bytes and decode as UTF-8.
 * Aborts (via cancelling the reader) when accumulated chunks exceed
 * the cap, so a chunked / CORS-misconfigured / missing-Content-Length
 * response cannot drag the tab into multi-megabyte buffering even if
 * the header pre-flight check passed.
 *
 * Throws a "too large" Error (sized from `cap`) when the cap is breached.
 */
async function readBodyWithCap(
  res: Response,
  cap: number,
): Promise<CappedBody> {
  if (!res.body) {
    // jsdom and some legacy environments do not expose Response.body.
    // Fall back to text() with a post-decode length check; not as
    // tight as streaming but still catches gross overshoot.
    const text = await res.text()
    if (text.length > cap) {
      throw tooLargeError(cap)
    }
    // No wire byte count is available on this path, so re-encode to get the
    // real UTF-8 size. `text.length` would be code units, which is off in both
    // directions — ~0.5x for ASCII-dominant HTML and ~3x low for BMP CJK — and
    // a budget that mis-sizes Chinese previews by 3x is not a budget. The extra
    // pass only ever runs where `Response.body` is missing (jsdom, some
    // embedded webviews), never in a browser that can render a preview.
    return { text, bytes: new TextEncoder().encode(text).length }
  }

  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let total = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      total += value.byteLength
      if (total > cap) {
        // Stop the network read; the response is too big to preview.
        await reader.cancel()
        throw tooLargeError(cap)
      }
      chunks.push(value)
    }
  } finally {
    reader.releaseLock?.()
  }

  const merged = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.byteLength
  }
  return {
    text: new TextDecoder('utf-8', { fatal: false }).decode(merged),
    bytes: total,
  }
}

/**
 * The preview fetch itself: admission claim, capped streaming read, exact-byte
 * measurement. Unchanged in behaviour by the supersession fence around it —
 * every failure path still releases its reservation before throwing, and the
 * caller decides only whether a throw is still allowed to speak for the key.
 */
async function runPreviewFetch(
  key: string,
  url: string | null,
  maxBytes: number,
  required: boolean,
): Promise<string> {
  if (!url) throw new Error('missing url')
  // Authoritative admission. The render-phase check in the hook is only a cheap
  // filter — React renders every thumbnail before running any effect, so
  // on first paint they all see the same empty budget and all admit
  // themselves. This claim runs after render, once per key, and its
  // synchronous prologue completes before the next fetcher's begins, so
  // simultaneous starts see each other and the burst stops at the budget.
  if (!reservePreviewSourceFetch(key, maxBytes, required)) {
    throw new FilePreviewError(
      'deferred',
      'Preview deferred: the page preview memory budget is full.',
    )
  }
  let res: Response
  try {
    res = await fetch(url, {
      signal: AbortSignal.timeout(PREVIEW_FETCH_TIMEOUT_MS),
    })
  } catch (fetchError) {
    // Hand the reserved capacity back on every failure path — a fetch that
    // never lands must not hold budget for the rest of the session.
    releasePreviewSourceReservation(key)
    if (isNetworkFailure(fetchError)) {
      throw new FilePreviewError(
        'network',
        'Network request failed while loading the preview.',
      )
    }
    throw fetchError
  }
  if (!res.ok) {
    releasePreviewSourceReservation(key)
    throw new FilePreviewError(
      'http',
      `Failed to fetch preview (${res.status})`,
      res.status,
    )
  }
  // Fast path: header-declared size already too big — abort before
  // we buffer anything. Trustworthy when present, but absent or
  // wrong on chunked / CORS-misconfigured responses, so the stream
  // reader below is the real enforcement point.
  const contentLength = res.headers.get('content-length')
  if (contentLength && Number(contentLength) > maxBytes) {
    releasePreviewSourceReservation(key)
    throw tooLargeError(maxBytes)
  }
  let body: CappedBody
  try {
    body = await readBodyWithCap(res, maxBytes)
  } catch (readError) {
    releasePreviewSourceReservation(key)
    throw readError
  }
  // MEASURE only — the EXACT streamed byte count, not the decoded string
  // length. The budget is not charged here: SWR has not yet decided
  // whether to keep this response (a `mutate` that overtook the
  // revalidation makes it discard the body), so charging now would let an
  // eviction racing an in-flight fetch leave the registry over-accounting
  // and evicting genuinely-cached entries. The charge happens once the hook
  // sees SWR's committed `data`.
  measurePreviewSourceBytes(key, body.bytes)
  return body.text
}

/**
 * Fetch text content of a previewable chat attachment for inline rendering —
 * markdown, code, json, html source.
 * Returns null while loading, an Error while failed, and the text content
 * on success.
 *
 * URL-agnostic by design: the caller decides where the bytes live. Today
 * callers pass a same-origin proxy URL
 * (`/api/messaging/web/attachments/{id}/serve`). The proxy follows the
 * backend's 302 to the GCS signed URL server-side and streams the body
 * back, so the response stays same-origin and CORS does not apply — the
 * older 302-passthrough implementation broke this and required a fix
 * (see those route handlers' docstrings for the rationale).
 *
 * Failure modes are classified into {@link FilePreviewError} reasons —
 * ``network`` (fetch rejection / timeout / offline), ``http`` (non-2xx,
 * including expired signed URLs propagated through the proxy), and
 * ``too_large``. Network errors are auto-retried by SWR (2 attempts,
 * exponential backoff); every failure can also be retried manually via
 * the returned ``retry``.
 *
 * Binary previews (image / video / audio / pdf / xlsx) should NOT use
 * this hook — render them directly with the URL so the browser native
 * element streams the bytes.
 */
export function useFilePreviewText(
  url: string | null,
  maxBytes: number = TEXT_PREVIEW_MAX_BYTES,
  options: {
    /**
     * User-initiated previews (anything the user explicitly opened) bypass the
     * page memory budget's admission gate. Thumbnails must not — they are the
     * unbounded population the budget exists to bound.
     */
    required?: boolean
  } = {},
): {
  data: string | null
  isLoading: boolean
  error: Error | null
  retry: () => void
  /** Attach to the always-mounted thumbnail shell for visibility pinning. */
  ref: (node: Element | null) => void
} {
  const required = options.required ?? false
  // Optional previews are admitted and pinned only while near the viewport.
  // A non-virtualized message list keeps every thumbnail component mounted,
  // so component lifetime is not evidence that its source is still visible.
  const [visible, setVisible] = useState(required)
  const visibilityCleanupRef = useRef<(() => void) | null>(null)
  const visibilityRef = useCallback(
    (node: Element | null) => {
      visibilityCleanupRef.current?.()
      visibilityCleanupRef.current = null
      if (required) {
        setVisible(true)
        return
      }
      if (!node) {
        setVisible(false)
        return
      }
      visibilityCleanupRef.current = observePreviewElementVisibility(
        node,
        setVisible,
      )
    },
    [required],
  )
  useEffect(
    () => () => {
      visibilityCleanupRef.current?.()
      visibilityCleanupRef.current = null
    },
    [],
  )

  // Key construction is shared with external eviction — see filePreviewTextKey.
  const requestedKey = url ? filePreviewTextKey(url, maxBytes) : null
  // Re-evaluate admission whenever the registry crosses the budget line, so a
  // deferred preview starts fetching once memory frees instead of waiting for
  // an unrelated re-render. Narrow channel on purpose: the heavy-viewer
  // channel fires on every slot transition, which would re-render every text
  // preview on the page for something that cannot affect them.
  useSyncExternalStore(
    subscribePreviewSourceBudget,
    getPreviewSourceBudgetVersion,
    getPreviewSourceBudgetVersion,
  )
  // `maxBytes` is passed so this asks the SAME question the fetcher asks. A
  // render gate that only checked "is there any room" said yes in the band
  // where the fetcher said no, and since nothing re-runs a fetcher on an
  // unchanged key, the resulting `deferred` error was permanent.
  const admitted =
    requestedKey === null ||
    !visible ||
    canAdmitPreviewSource(requestedKey, maxBytes, required)
  // A deferred preview must not fetch, must not pin, and must not create a
  // cache entry — a null key is how all three are expressed at once.
  const swrKey = visible && admitted ? requestedKey : null
  const { mutate: globalMutate, cache } = useSWRConfig()
  // One stable eviction closure per key, handed to the budget registry so each
  // accounted entry carries its own pre-bound single-key `mutate`. `cache` is
  // bound alongside it because a complete eviction must also invalidate this
  // key's request markers, and those live per cache instance.
  const evict = useMemo(
    () =>
      swrKey
        ? bindFilePreviewTextEviction(globalMutate, swrKey, cache)
        : NOOP_EVICT,
    [globalMutate, cache, swrKey],
  )

  const { data, error, isLoading, mutate } = useSWR<string, Error>(
    swrKey,
    async (key: string) => {
      // Claim the key for this call. Only the holder may report a failure —
      // see `inFlightPreviewFetches` for why SWR cannot fence this itself.
      const ticket = {}
      inFlightPreviewFetches.set(key, ticket)
      try {
        return await runPreviewFetch(key, url, maxBytes, required)
      } catch (fetchError) {
        // Evicted, or overtaken by a newer fetch: this call no longer speaks
        // for the key. Returning (rather than rethrowing) is what keeps it
        // silent; SWR discards the value on its success-side staleness check.
        if (inFlightPreviewFetches.get(key) !== ticket) {
          return SUPERSEDED_PREVIEW_FETCH
        }
        throw fetchError
      } finally {
        // Never clear a ticket that is no longer ours.
        if (inFlightPreviewFetches.get(key) === ticket) {
          inFlightPreviewFetches.delete(key)
        }
      }
    },
    {
      revalidateOnFocus: false,
      onErrorRetry: (retryError, _key, _config, revalidate, { retryCount }) => {
        const delay = nextPreviewRetryDelayMs(retryError, retryCount)
        if (delay == null) return
        setTimeout(() => {
          void revalidate({ retryCount })
        }, delay)
      },
    },
  )

  // Pin the entry for as long as this consumer is mounted. Unmount only
  // UNPINS — it never evicts (design §3-C): a virtualized message list scrolls
  // consumers in and out constantly, and evict-on-unmount would thrash the
  // fetches. Eviction is the registry's job, in LRU order, over budget only.
  // retain/release are exact inverses, so StrictMode's double-invoked mount
  // effect settles at the same refCount as a single invocation.
  //
  // Keyed on `swrKey` alone. Depending on `evict` too would tear the pin down
  // and rebuild it whenever that closure is re-created, and the transient
  // refCount 0 in between could evict an entry that is on screen.
  useEffect(() => {
    if (!swrKey) return
    retainPreviewSource(swrKey)
    return () => {
      releasePreviewSource(swrKey)
    }
  }, [swrKey])

  // Charge the measured bytes to the budget once SWR reports committed data —
  // `data` being present is the proof that the string is really resident, and
  // is the only honest moment to account it (see the fetcher above).
  useEffect(() => {
    if (!swrKey) return
    if (data != null) {
      observePreviewSource(swrKey, evict)
      return
    }
    // Settled with nothing committed. The fetcher may still have completed and
    // had its body discarded by SWR (a `mutate` overtook the revalidation),
    // in which case the reservation it took is held by an entry that will
    // never be charged — capacity lost for as long as this component lives.
    // `isLoading` is false only once SWR has stopped trying, so this cannot
    // reclaim capacity from a fetch that is genuinely still running, and
    // handing back a reservation that was already released is a no-op.
    if (!isLoading) releasePreviewSourceReservation(swrKey)
  }, [swrKey, data, isLoading, evict])

  // Surfaced as a classified error rather than a permanent spinner: callers
  // already render their static placeholder for the error branch, which is the
  // degradation we want, and `nextPreviewRetryDelayMs` only auto-retries
  // `network`, so a deferred preview does not busy-loop.
  const deferredError =
    requestedKey !== null && visible && !admitted
      ? new FilePreviewError(
          'deferred',
          'Preview deferred: the page preview memory budget is full.',
        )
      : null

  return {
    data: data ?? null,
    isLoading: deferredError ? false : isLoading,
    error: deferredError ?? error ?? null,
    // No-arg mutate() revalidates the same key — equivalent to closing
    // and reopening the preview.
    retry: () => {
      void mutate()
    },
    ref: visibilityRef,
  }
}
